import { Button, Typography } from '@themenu/design-system'
import { type ChangeEvent, type FC, type FormEvent, useId, useState } from 'react'
import { useIntl } from 'react-intl'

import {
  FormikFieldInputPassword,
  type FormikFieldInputPasswordProps,
} from '../../../components/Form/FormikFieldInputPassword'
import { environmentVariables } from '../../../utils/environmentVariables'
import { ActivationStepLayout } from './ActivationStepLayout'

// FormikProps/FieldMetaProps require ~30 unrelated fields we can't provide outside a Formik context
const toStandaloneFieldProps = (
  name: string,
  value: string,
): Pick<FormikFieldInputPasswordProps, 'field' | 'form' | 'meta'> =>
  // biome-ignore lint/suspicious/noExplicitAny: standalone field props don't satisfy formik's full types
  ({
    field: { name, value },
    form: { touched: {}, errors: {} },
    meta: { touched: false, error: undefined },
  }) as any

export type PasswordCredentialStepProps = {
  allowPin: boolean
  onSubmit: (credential: string) => Promise<void>
  errorMessageId?: string | null
  onBack: () => void
}

export const PasswordCredentialStep: FC<PasswordCredentialStepProps> = ({
  allowPin,
  onSubmit,
  errorMessageId = null,
  onBack,
}) => {
  const { formatMessage } = useIntl()
  const descriptionId = useId()

  const [credential, setCredential] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const descriptionMessageId = allowPin
    ? 'br_activation.password_credential.description_dual'
    : 'br_activation.password_credential.description_password'
  const labelMessageId = allowPin
    ? 'br_activation.password_credential.label_dual'
    : 'br_activation.password_credential.label_password'

  const isValid = credential.length > 0

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!isValid || submitting) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit(credential)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ActivationStepLayout
      titleId="br_activation.password_credential.title"
      errorMessageId={errorMessageId}
      showBackButton
      onBack={onBack}
      onSubmit={handleSubmit}
      testId="br-activation-password-credential-form"
      footer={
        <>
          <Button
            data-testid="br-activation-password-credential-submit"
            type="submit"
            variant="primary"
            className="w-full"
            disabled={!isValid || submitting}
            loading={submitting}
          >
            {formatMessage({ id: 'br_activation.password_credential.submit' })}
          </Button>
          <Button
            data-testid="br-activation-password-credential-forgotten"
            variant="secondary"
            className="w-full"
            component="a"
            href={environmentVariables('BR_HELP_URL')}
          >
            {formatMessage({ id: 'br_activation.password_credential.forgotten' })}
          </Button>
        </>
      }
    >
      <Typography variant="body" id={descriptionId}>
        {formatMessage({ id: descriptionMessageId })}
      </Typography>
      <FormikFieldInputPassword
        {...toStandaloneFieldProps('password_credential', credential)}
        innerRef={{ current: null }}
        id="br-activation-password-credential"
        data-testid="br-activation-password-credential"
        type="password"
        spellCheck="false"
        autoComplete="current-password"
        aria-describedby={descriptionId}
        label={formatMessage({ id: labelMessageId })}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setCredential(e.target.value)}
      />
    </ActivationStepLayout>
  )
}
